import { makeStyles, Slider } from "@material-ui/core";
import { useEffect, useState, useCallback } from "react";

const questions = [
  {
    key: "fatigue",
    text: "How would you describe the overall level of fatigue/tiredness you have experienced?",
  },
  {
    key: "asNBH",
    text: "How would you describe the overall level of AS neck, back or hip pain you have had?",
  },
  {
    key: "jointNBH",
    text: "How would you describe the overall level of pain/swelling in joints other than neck, back or hips you have had?",
  },
  {
    key: "areaTender",
    text: "How would you describe the overall level of discomfort you have had from any areas tender to touch or pressure?",
  },
  {
    key: "msLevel",
    text: "How would you describe the overall level of discomfort you have had from the time you wake up?",
  },
  {
    key: "msTime",
    text: "How long does your morning stiffness last from the time you wake up?",
  },
];

const defaultAssessment = {
  fatigue: 1,
  asNBH: 1,
  jointNBH: 1,
  areaTender: 1,
  msLevel: 1,
  msTime: 1,
};

const marks = Array.from({ length: 10 }, (_, i) => ({
  value: i + 1,
  label: (i + 1).toString(),
}));

const msTimeMarks = [
  { value: 1, label: "0 hr" },
  { value: 5, label: "1 hr" },
  { value: 10, label: "2+ hrs" },
];

const BasdaiAssessment = ({
  appointmentId,
  pid,
  token,
  currentBasdaiInfo,
  onBasdaiInfoUpdate,
  onError,
}) => {
  const [assessmentDetail, setAssessmentDetail] = useState(defaultAssessment);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);

  // Fetch current BASDAI score and details (only if not already available)
  const fetchBasdaiPainScore = useCallback(async () => {
    if (!pid || !token) return;

    try {
      const response = await fetch(`/api/v1/pain-score/basdai/${pid}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (responseData?.status?.success) {
        responseData?.current && onBasdaiInfoUpdate(responseData.current);
      } else {
        onError(new Error("Failed to fetch BASDAI score"), responseData);
      }
    } catch (error) {
      onError(error);
    } finally {
    }
  }, [pid, token, onBasdaiInfoUpdate, onError]);

  // Calculate and update BASDAI score
  const calculateAndUpdateBasdai = useCallback(
    async (assessmentData) => {
      if (!appointmentId || !token) return;

      setIsCalculating(true);
      try {
        const response = await fetch(
          `/api/v1/pain-score/basdai/${appointmentId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(assessmentData),
          }
        );

        const responseData = await response.json();

        if (responseData?.status?.success) {
          // Refetch the updated score after a short delay
          await fetchBasdaiPainScore();
        } else {
          onError(new Error("Failed to calculate BASDAI score"), responseData);
        }
      } catch (error) {
        onError(error);
      } finally {
        setIsCalculating(false);
      }
    },
    [appointmentId, token, fetchBasdaiPainScore, onError]
  );

  useEffect(() => {
    if (currentBasdaiInfo?.value && currentBasdaiInfo?.scoreDetail) {
      return; // Skip fetch if data already available
    }

    setIsInitialLoading(true);
    fetchBasdaiPainScore().finally(() => {
      setIsInitialLoading(false);
    });
  }, [fetchBasdaiPainScore]); //

  useEffect(() => {
    // Skip initial fetch if both score and assessment details are already available
    if (currentBasdaiInfo?.value && currentBasdaiInfo?.scoreDetail) {
      // Use provided data to populate assessment details
      setAssessmentDetail({
        fatigue: currentBasdaiInfo.scoreDetail.fatigue || 1,
        asNBH: currentBasdaiInfo.scoreDetail.asNBH || 1,
        jointNBH: currentBasdaiInfo.scoreDetail.jointNBH || 1,
        areaTender: currentBasdaiInfo.scoreDetail.areaTender || 1,
        msLevel: currentBasdaiInfo.scoreDetail.msLevel || 1,
        msTime: currentBasdaiInfo.scoreDetail.msTime || 1,
      });
    }
  }, [currentBasdaiInfo]);

  // Handle slider changes
  const handleSliderChange = useCallback((key, newValue) => {
    setAssessmentDetail((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  }, []);

  // Styles
  const useStyles = makeStyles({
    root: { height: 10, marginTop: 10 },
    thumb: {
      height: 24,
      width: 24,
      backgroundColor: "var(--darkbluecolorone)",
      marginTop: -10,
      marginLeft: -12,
    },
    track: {
      height: 5,
      borderRadius: 5,
      backgroundColor: "var(--darkbluecolorone)",
    },
    rail: {
      height: 5,
      borderRadius: 5,
    },
    valueLabel: {
      left: "calc(-50% + 8px)",
      top: -30,
      color: "var(--darkbluecolorone)",
    },
  });
  const sliderClasses = useStyles();

  const useComponentStyles = makeStyles({
    label: {
      fontSize: "16px",
      lineHeight: "21px",
      fontWeight: "normal",
      fontStyle: "normal",
      letterSpacing: "0px",
      color: "#000000CC",
      opacity: 1,
    },
    heading: {
      color: "#0060A6",
      backgroundColor: "#E8ECEF",
      fontSize: "18px",
    },
    scoreBlock: {
      backgroundColor: "#0060A6",
      color: "#FFFFFF",
      padding: "10px 20px",
      fontSize: "20px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
    },
    loadingContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "20px",
    },
  });
  const componentClasses = useComponentStyles();

  if (isInitialLoading) {
    return (
      <div className={componentClasses.loadingContainer}>
        <div id="loader" />
      </div>
    );
  }

  return (
    <>
      <div className={componentClasses.heading + " text-center px-4 py-3"}>
        The Bath Ankylosing Spondylitis Disease Activity Index
      </div>

      <div className="text-center px-4 mt-2">
        <span className="fw-bold">Pain on a 10 point scale</span>
        <br />
        <span>(1 is none and 10 is the worst)</span>
      </div>

      <div className="container p-4">
        {questions.map((question) => (
          <div className="mb-4" key={question.key}>
            <label className={componentClasses.label}>{question.text}</label>
            <Slider
              classes={sliderClasses}
              value={assessmentDetail[question.key]}
              onChange={(_, newValue) =>
                handleSliderChange(question.key, newValue)
              }
              step={1}
              min={1}
              max={10}
              marks={question.key === "msTime" ? msTimeMarks : marks}
              valueLabelDisplay="auto"
              disabled={isCalculating}
            />
          </div>
        ))}

        <div className="mb-4">
          <div className="btn_block">
            <button
              className="btn btn_primary m-auto"
              onClick={() => calculateAndUpdateBasdai(assessmentDetail)}
              disabled={isCalculating || !appointmentId}
            >
              {isCalculating ? "Calculating..." : "Calculate"}
            </button>
          </div>
        </div>
      </div>

      <div className={componentClasses.scoreBlock}>
        <span className="fw-bold">BASDAI Score:</span>
        <span className="fw-bold">
          {currentBasdaiInfo?.value !== null &&
          currentBasdaiInfo?.value !== undefined
            ? currentBasdaiInfo.value
            : "—"}
        </span>
      </div>
    </>
  );
};

export default BasdaiAssessment;
